import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
  Typography,
  useTheme,
  alpha,
  useMediaQuery,
  Link,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Visibility, VisibilityOff, Email, Key, ArrowBack } from '@mui/icons-material';
import { FaUsers, FaImage, FaGoogle, FaApple, FaLinkedinIn } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { LoginSignupState } from 'types/loginSignUpTypes';
import { login, verifyOtp, clearToast } from 'store/slices/loginSignupSlice';
import { sendPasswordResetOTP, resetPasswordWithOTP, continueWithGoogle, type GoogleAuthResponse } from 'api/auth';
import { GoogleLogin } from '@react-oauth/google';
import axiosInstance from 'Services/APIService';
import JoinCommunity from './JoinCommunity';

interface LoginData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface LoginProps {
  onBack: () => void;
  colors: {
    primary: string;
    secondary: string;
    textSecondary: string;
    textPrimary: string;
  };
}

const schema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(5, 'Password must be at least 5 characters').required('Password is required'),
  rememberMe: yup.boolean()
});

const Login: React.FC<LoginProps> = ({ onBack, colors }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [step, setStep] = useState(1); // 1: login, 2: OTP, 3: forgot password email, 4: reset password
  const [emailCode, setEmailCode] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [showJoinCommunity, setShowJoinCommunity] = useState(false);
  const { loading, toast, isAuthenticated } = useSelector(
    (state: { loginSignup: LoginSignupState }) => state.loginSignup
  );
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginData>({
    resolver: yupResolver(schema),
    defaultValues: {
      rememberMe: false
    }
  });

  // Handle navigation after successful authentication
  useEffect(() => {
    if (isAuthenticated && step === 2) {
      setTimeout(() => {
        navigate('/services');
        dispatch(clearToast());
      }, 1500);
    }
  }, [isAuthenticated, step, navigate, dispatch]);

  // Clear toast after display
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        dispatch(clearToast());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast, dispatch]);

  const onSubmit = (data: LoginData) => {
    dispatch(login({ username: data.email, password: data.password, sso: false }) as any).then(
      (result: any) => {
        if (result.meta.requestStatus === 'fulfilled') {
          setEmail(data.email);
          setStep(2);
        }
      }
    );
  };

  const handleVerifyCode = () => {
    dispatch(verifyOtp({ username: email, otp: emailCode }) as any);
  };

  const handleOtpChange = (e: any, index: number) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 1) {
      const newCode = emailCode.split('');
      newCode[index] = value;
      const joinedCode = newCode.join('');
      setEmailCode(joinedCode);
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  useEffect(() => {
    if (emailCode.length === 6 && /^\d{6}$/.test(emailCode)) {
      handleVerifyCode();
    }
  }, [emailCode]);

  const handleKeyDown = (e: any, index: number) => {
    if (e.key === 'Backspace' && !emailCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleResendCode = () => {
    setEmailCode('');
    dispatch(login({ username: email, password: '', sso: false }) as any);
  };

  const handleForgotPassword = () => {
    setStep(3);
    setForgotError('');
    setForgotSuccess('');
  };

  const handleSendResetOTP = async () => {
    if (!forgotEmail) {
      setForgotError('Please enter your email');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await sendPasswordResetOTP(forgotEmail);
      setForgotSuccess(response.message || 'OTP sent to your email');
      setStep(4);
    } catch (error: any) {
      setForgotError(error.error || 'Failed to send OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetOtp || !newPassword || !confirmPassword) {
      setForgotError('Please fill all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setForgotError('Password must be at least 8 characters');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await resetPasswordWithOTP(forgotEmail, resetOtp, newPassword, confirmPassword);
      setForgotSuccess(response.message || 'Password reset successfully');
      setTimeout(() => {
        setStep(1);
        setForgotEmail('');
        setResetOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setForgotSuccess('');
      }, 2000);
    } catch (error: any) {
      setForgotError(error.error || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const [googleCredential, setGoogleCredential] = useState<any>(null);
  const [showGoogleProfileForm, setShowGoogleProfileForm] = useState(false);
  const [showGroupsSelection, setShowGroupsSelection] = useState(false);
  const [googleMissingFields, setGoogleMissingFields] = useState<string[]>([]);
  const [googleTokenTimestamp, setGoogleTokenTimestamp] = useState<number>(0);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [googleProfileSubmitLoading, setGoogleProfileSubmitLoading] = useState(false);
  const [googleProfileData, setGoogleProfileData] = useState({
    phone: '',
    surburb: '',
    state: '',
    country_res: '',
    postcode: '',
    groups: [] as string[]
  });

  // Fetch groups from API when profile form is shown
  useEffect(() => {
    const fetchGroupsForGoogle = async () => {
      if (showGoogleProfileForm && googleMissingFields.includes('groups') && availableGroups.length === 0) {
        setGroupsLoading(true);
        try {
          const response = await axiosInstance.get('/public/get-groups-list');
          setAvailableGroups(response.data.groups || []);
        } catch (error) {
          setAvailableGroups([]);
        } finally {
          setGroupsLoading(false);
        }
      }
    };
    fetchGroupsForGoogle();
  }, [showGoogleProfileForm, googleMissingFields]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      if (!credentialResponse.credential) {
        throw new Error('No credential provided from Google');
      }
      
      setGoogleCredential(credentialResponse.credential);
      setGoogleTokenTimestamp(Date.now());
      
      const result = await continueWithGoogle(credentialResponse.credential);
      
      // Response now returns user data directly (no success wrapper)
      if (result && result.user) {
        // Extract role from user.role (array of role objects)
        const roles = result.user.role || [];
        
        const userDetails = {
          email: result.user.email,
          name: result.user.name,
          userId: result.userId,
          token: result.token,
          groups: result.groups || [],
          role: roles,  // Array of role objects with role_id and label
          avatar: result.avatar || '',
          user: result.user
        };
        localStorage.setItem('user-details', JSON.stringify(userDetails));
        
        if (result.profile_complete) {
          navigate('/services');
        } else {
          setGoogleMissingFields(result.missing_fields || []);
          setShowGoogleProfileForm(true);
        }
      } else {
        const errorMsg = result?.message || result?.error || 'Google login failed';
        alert(errorMsg);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to process Google login. Please try again.';
      alert(errorMessage);
    }
  };

  const handleGoogleProfileSubmit = async () => {
    try {
      if (!googleCredential) {
        alert('Session expired. Please click "Sign in with Google" button again.');
        setShowGoogleProfileForm(false);
        return;
      }
      
      // Validate groups if required
      if (googleMissingFields.includes('groups') && googleProfileData.groups.length === 0) {
        alert('Please select at least one group');
        return;
      }
      
      setGoogleProfileSubmitLoading(true);
      const result = await continueWithGoogle(googleCredential, googleProfileData);
      
      // Response now returns user data directly (no success wrapper)
      if (result && result.user) {
        // Extract role from user.role (array of role objects)
        const roles = result.user.role || [];
        
        const userDetails = {
          email: result.user.email,
          name: result.user.name,
          userId: result.userId,
          token: result.token,
          groups: result.groups || [],
          role: roles,  // Array of role objects with role_id and label
          avatar: result.avatar || '',
          user: result.user
        };
        localStorage.setItem('user-details', JSON.stringify(userDetails));
        
        setShowGoogleProfileForm(false);
        setGoogleCredential(null);
        setAvailableGroups([]);
        setGoogleProfileData({
          phone: '',
          surburb: '',
          state: '',
          country_res: '',
          postcode: '',
          groups: [] as string[]
        });
        navigate('/services');
      } else {
        const errorMsg = result?.message || result?.error || 'Failed to update profile';
        alert(errorMsg);
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.error || error?.response?.data?.message || 'Failed to complete profile. Please try again.';
      alert(errorMessage);
    } finally {
      setGoogleProfileSubmitLoading(false);
    }
  };

  const handleGoogleError = () => {
    alert('Google login failed. Please try again.');
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '480px'
      }}
    >
      {/* Show Join Community flow if requested */}
      {showJoinCommunity ? (
        <JoinCommunity
          onBack={() => setShowJoinCommunity(false)}
          colors={colors}
        />
      ) : (
      <>
      {toast && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Alert
            severity={toast.severity}
            onClose={() => dispatch(clearToast())}
            sx={{
              mb: 2,
              borderRadius: 1,
              fontSize: '0.9rem'
            }}
          >
            {toast.message}
          </Alert>
        </motion.div>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} sx={{ color: colors.primary }} />
        </Box>
      ) : step === 1 ? (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          {/* Social Login Button - Google */}
          <Box sx={{ width: '100%' }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signin_with"
              width="100%"
            />
          </Box>

          {/* Or Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <Box sx={{ flex: 1, height: '1px', background: '#E4E4E7' }} />
            <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', fontWeight: 400 }}>
              Or
            </Typography>
            <Box sx={{ flex: 1, height: '1px', background: '#E4E4E7' }} />
          </Box>


          {/* Email Field - Match JoinCommunity style */}
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email Address"
                placeholder="Enter your email"
                value={field.value}
                onChange={field.onChange}
                size="small"
                sx={{ mb: 1 }}
              />
            )}
          />


          {/* Password Field - Match JoinCommunity style */}
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Enter Password"
                placeholder="Create a password"
                type={showPassword ? 'text' : 'password'}
                value={field.value}
                onChange={field.onChange}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 1 }}
              />
            )}
          />

          {/* Remember Me & Forgot Password */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
            <Controller
              name="rememberMe"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={<Checkbox size="small" checked={value || false} onChange={onChange} />}
                  label={<Typography sx={{ fontSize: '0.9rem', color: colors.textSecondary }}>Remember me</Typography>}
                />
              )}
            />
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleForgotPassword();
              }}
              sx={{
                fontSize: '0.9rem',
                color: colors.primary,
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Forgot password?
            </Link>
          </Box>

          {/* Sign In Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              py: 1.3,
              mt: 1,
              background: colors.primary,
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                background: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Sign In
          </Button>

          {/* Sign Up Link - Two Box Layout */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 1 }}>
            {/* Box 1: Question */}
            <Box>
              <Typography sx={{ textAlign: 'center', color: colors.textSecondary, fontSize: '0.95rem' }}>
                Don't have an account?
              </Typography>
            </Box>
            
            {/* Box 2: Action Link */}
            <Box>
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowJoinCommunity(true);
                }}
                sx={{
                  color: colors.primary,
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  display: 'inline-block',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    textDecoration: 'underline',
                    backgroundColor: alpha(colors.primary, 0.08)
                  }
                }}
              >
                Join a Community
              </Link>
            </Box>
          </Box>
        </Box>
      ) : step === 2 ? (
        // OTP Verification Step
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: colors.textPrimary }}>
            Enter OTP
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
            Verification code has been sent to {email}
          </Typography>

          {/* OTP Input Fields */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <TextField
                key={index}
                id={`otp-input-${index}`}
                value={emailCode[index] || ''}
                onChange={(e: any) => handleOtpChange(e, index)}
                onKeyDown={(e: any) => handleKeyDown(e, index)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }
                }}
                sx={{
                  width: '50px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                  }
                }}
              />
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyCode}
            sx={{
              py: 1.2,
              background: colors.primary,
              color: '#FFFFFF',
              fontWeight: 600,
              '&:hover': { background: colors.primary, opacity: 0.9 }
            }}
          >
            Verify
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={handleResendCode}
            sx={{
              mt: 1,
              color: colors.primary,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Resend Code
          </Button>
        </Box>
      ) : step === 3 ? (
        // Forgot Password - Enter Email Step
        <Box>
          <Button
            size="small"
            onClick={() => setStep(1)}
            sx={{ mb: 2, color: colors.primary }}
            startIcon={<ArrowBack />}
          >
            Back to Login
          </Button>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: colors.textPrimary }}>
            Forgot Password
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: colors.textSecondary }}>
            Enter your email address and we'll send you an OTP to reset your password.
          </Typography>

          {forgotError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {forgotError}
            </Alert>
          )}

          {forgotSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {forgotSuccess}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            placeholder="Enter your email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            size="small"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontSize: '1rem',
                padding: '12px 16px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1rem',
                color: '#999'
              }
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleSendResetOTP}
            disabled={forgotLoading}
            sx={{
              py: 1.3,
              background: colors.primary,
              color: '#FFFFFF',
              fontWeight: 600,
              '&:hover': { background: colors.primary, opacity: 0.9 }
            }}
          >
            {forgotLoading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
          </Button>
        </Box>
      ) : step === 4 ? (
        // Reset Password - Enter OTP and New Password Step
        <Box sx={{ maxHeight: '520px', overflowY: 'auto', pr: 1 }}>
          <Button
            size="small"
            onClick={() => setStep(3)}
            sx={{ mb: 1.5, color: colors.primary }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>

          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: colors.textPrimary }}>
            Reset Password
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: colors.textSecondary }}>
            Enter the OTP sent to {forgotEmail} and your new password.
          </Typography>

          {forgotError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {forgotError}
            </Alert>
          )}

          {forgotSuccess && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {forgotSuccess}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Enter OTP"
            placeholder="Enter 6-digit OTP"
            value={resetOtp}
            onChange={(e) => setResetOtp(e.target.value)}
            size="small"
            inputProps={{ maxLength: 6 }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            placeholder="Enter new password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            placeholder="Confirm new password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleResetPassword}
            disabled={forgotLoading}
            sx={{
              py: 1.2,
              background: colors.primary,
              color: '#FFFFFF',
              fontWeight: 600,
              '&:hover': { background: colors.primary, opacity: 0.9 }
            }}
          >
            {forgotLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
          </Button>
        </Box>
      ) : null}

      {/* Google Profile Form Modal - Only show missing fields */}
      <Dialog 
        open={showGoogleProfileForm} 
        onClose={() => setShowGoogleProfileForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: colors.textPrimary }}>
          Complete Your Profile
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            Please fill in the following information to complete your profile.
          </Typography>

          {googleMissingFields.includes('phone') && (
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="Enter your phone number"
              type="tel"
              value={googleProfileData.phone}
              onChange={(e) => setGoogleProfileData(prev => ({ ...prev, phone: e.target.value }))}
              size="small"
              sx={{ mb: 1.5 }}
            />
          )}

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            {googleMissingFields.includes('surburb') && (
              <Grid item xs={googleMissingFields.includes('state') ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Suburb"
                  placeholder="Enter suburb"
                  value={googleProfileData.surburb}
                  onChange={(e) => setGoogleProfileData(prev => ({ ...prev, surburb: e.target.value }))}
                  size="small"
                />
              </Grid>
            )}
            {googleMissingFields.includes('state') && (
              <Grid item xs={googleMissingFields.includes('surburb') ? 6 : 12}>
                <TextField
                  fullWidth
                  label="State"
                  placeholder="Enter state"
                  value={googleProfileData.state}
                  onChange={(e) => setGoogleProfileData(prev => ({ ...prev, state: e.target.value }))}
                  size="small"
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            {googleMissingFields.includes('country_res') && (
              <Grid item xs={googleMissingFields.includes('postcode') ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Country"
                  placeholder="Enter country"
                  value={googleProfileData.country_res}
                  onChange={(e) => setGoogleProfileData(prev => ({ ...prev, country_res: e.target.value }))}
                  size="small"
                />
              </Grid>
            )}
            {googleMissingFields.includes('postcode') && (
              <Grid item xs={googleMissingFields.includes('country_res') ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Postcode"
                  placeholder="Enter postcode"
                  value={googleProfileData.postcode}
                  onChange={(e) => setGoogleProfileData(prev => ({ ...prev, postcode: e.target.value }))}
                  size="small"
                />
              </Grid>
            )}
          </Grid>

          {/* Groups Selection - Only show if required */}
          {googleMissingFields.includes('groups') && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: colors.textPrimary }}>
                Select Groups *
              </Typography>
              <Box sx={{ 
                p: 1.5, 
                border: `1px solid #E4E4E7`, 
                borderRadius: 1, 
                maxHeight: '280px', 
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: '#F5F5F5' },
                '&::-webkit-scrollbar-thumb': { background: '#C4C4C7', borderRadius: '3px' }
              }}>
                {groupsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : availableGroups && availableGroups.length > 0 ? (
                  <Grid container spacing={1}>
                    {availableGroups.map((group: any) => {
                      const groupId = group.group_id || group.id;
                      const isSelected = googleProfileData.groups.includes(groupId);
                      return (
                        <Grid item xs={6} key={groupId}>
                          <Box
                            onClick={() => {
                              if (isSelected) {
                                setGoogleProfileData({
                                  ...googleProfileData,
                                  groups: googleProfileData.groups.filter(g => g !== groupId)
                                });
                              } else {
                                setGoogleProfileData({
                                  ...googleProfileData,
                                  groups: [...googleProfileData.groups, groupId]
                                });
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              border: isSelected ? `2px solid ${colors.primary}` : `1px solid #E4E4E7`,
                              background: isSelected ? `${colors.primary}15` : '#FFFFFF',
                              borderRadius: 1,
                              p: 1.2,
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: colors.primary,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              }
                            }}
                          >
                            {/* Group Avatar */}
                            {group.avatar ? (
                              <Box
                                component="img"
                                src={group.avatar}
                                alt={group.label || group.name}
                                sx={{
                                  width: '100%',
                                  height: '70px',
                                  borderRadius: '6px',
                                  objectFit: 'cover',
                                  mb: 0.8
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '70px',
                                  borderRadius: '6px',
                                  mb: 0.8,
                                  background: `${colors.primary}20`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaImage size={28} color={colors.primary} opacity={0.5} />
                              </Box>
                            )}
                            
                            {/* Group Name */}
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.85rem',
                                mb: 0.5,
                                color: colors.textPrimary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {group.label || group.name}
                            </Typography>
                            
                            {/* Description */}
                            {group.description && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: colors.textSecondary,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  mb: 0.5,
                                  fontSize: '0.75rem'
                                }}
                              >
                                {group.description}
                              </Typography>
                            )}
                            
                            {/* People Count */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.6 }}>
                              <FaUsers size={12} color={colors.textSecondary} />
                              <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
                                {group.peopleCount || 0} Members
                              </Typography>
                            </Box>
                            
                            {/* Checkbox */}
                            <Box sx={{ mt: 0.8 }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => {}}
                                size="small"
                                sx={{ p: 0 }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    No groups available
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setShowGoogleProfileForm(false);
              setGoogleCredential(null);
              setGoogleMissingFields([]);
            }}
            sx={{ color: colors.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleGoogleProfileSubmit}
            variant="contained"
            sx={{ background: colors.primary, color: '#FFFFFF' }}
            disabled={googleProfileSubmitLoading || (googleMissingFields.length > 0 && (
              (googleMissingFields.includes('phone') && !googleProfileData.phone) ||
              (googleMissingFields.includes('surburb') && !googleProfileData.surburb) ||
              (googleMissingFields.includes('state') && !googleProfileData.state) ||
              (googleMissingFields.includes('country_res') && !googleProfileData.country_res) ||
              (googleMissingFields.includes('postcode') && !googleProfileData.postcode)
            ))}
          >
            {googleProfileSubmitLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
                Completing...
              </Box>
            ) : (
              'Complete Profile'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

export default Login;
